import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type DownloadType = 'angles' | 'anglesPaddingJson' | 'clips';

type DownloadJob = {
  id: string;
  feedId: string;
  feedTitle: string;
  open: boolean;
  minimized: boolean;
  downloadType?: DownloadType;
  focusOrder: number;
};

type DownloadManagerState = {
  jobs: DownloadJob[];
  nextJobIndex: number;
  nextFocusOrder: number;
};

const initialState: DownloadManagerState = {
  jobs: [],
  nextJobIndex: 1,
  nextFocusOrder: 1,
};

const downloadTypes: DownloadType[] = ['angles', 'anglesPaddingJson', 'clips'];

const isVisibleJob = (job: DownloadJob) => job.open;

const nextFocusOrder = (state: DownloadManagerState) => {
  state.nextFocusOrder = (state.nextFocusOrder || 1) + 1;
  return state.nextFocusOrder;
};

const createJob = (state: DownloadManagerState, feedId: string, feedTitle: string, downloadType?: DownloadType): DownloadJob => {
  const jobIndex = state.nextJobIndex || state.jobs.length + 1;
  state.nextJobIndex = jobIndex + 1;

  return {
    id: `${feedId}-${jobIndex}`,
    feedId,
    feedTitle,
    open: true,
    minimized: false,
    downloadType,
    focusOrder: nextFocusOrder(state),
  };
};

const downloadManagerSlice = createSlice({
  name: 'downloadManager',
  initialState,
  reducers: {
    openDownloadManager(state, action: PayloadAction<{ feedId: string; feedTitle: string }>) {
      const feedId = String(action.payload.feedId);
      const existing = state.jobs
        .filter((j) => j.feedId === feedId && isVisibleJob(j))
        .sort((a, b) => (b.focusOrder || 0) - (a.focusOrder || 0))[0];

      if (existing) {
        state.jobs.forEach((job) => {
          if (job.feedId === feedId && isVisibleJob(job)) {
            job.feedTitle = action.payload.feedTitle;
            job.minimized = false;
          }
        });
        existing.open = true;
        existing.feedTitle = action.payload.feedTitle;
        existing.minimized = false;
        existing.focusOrder = nextFocusOrder(state);
      } else {
        const reusable = state.jobs.find((j) => j.feedId === feedId && !isVisibleJob(j));

        if (reusable) {
          reusable.open = true;
          reusable.minimized = false;
          reusable.feedTitle = action.payload.feedTitle;
          reusable.downloadType = 'angles';
          reusable.focusOrder = nextFocusOrder(state);
        } else {
          state.jobs.push(createJob(state, feedId, action.payload.feedTitle, 'angles'));
        }
      }
    },
    addDownloadManagerLayer(state, action: PayloadAction<{ feedId: string; feedTitle: string }>) {
      const feedId = String(action.payload.feedId);
      const visibleJobs = state.jobs.filter((j) => j.feedId === feedId && isVisibleJob(j));
      if (visibleJobs.length >= downloadTypes.length) return;

      const usedTypes = new Set(visibleJobs.map((j) => j.downloadType).filter(Boolean));
      const nextType = downloadTypes.find((type) => !usedTypes.has(type));
      if (!nextType) return;

      const reusable = state.jobs.find((j) => j.feedId === feedId && !isVisibleJob(j));
      if (reusable) {
        reusable.open = true;
        reusable.minimized = false;
        reusable.feedTitle = action.payload.feedTitle;
        reusable.downloadType = nextType;
        reusable.focusOrder = nextFocusOrder(state);
      } else {
        state.jobs.push(createJob(state, feedId, action.payload.feedTitle, nextType));
      }
    },
    focusDownloadManager(state, action: PayloadAction<{ id: string }>) {
      const job = state.jobs.find((j) => j.id === action.payload.id);
      if (job) {
        state.jobs.forEach((item) => {
          if (item.feedId === job.feedId && isVisibleJob(item)) {
            item.minimized = false;
          }
        });
        job.open = true;
        job.minimized = false;
        job.focusOrder = nextFocusOrder(state);
      }
    },
    setDownloadManagerType(state, action: PayloadAction<{ id: string; downloadType: DownloadType }>) {
      const job = state.jobs.find((j) => j.id === action.payload.id);
      if (!job) return;

      const typeAlreadyUsed = state.jobs.some(
        (j) => j.id !== job.id && j.feedId === job.feedId && isVisibleJob(j) && j.downloadType === action.payload.downloadType,
      );
      if (typeAlreadyUsed) return;

      job.downloadType = action.payload.downloadType;
    },
    closeDownloadManager(state, action: PayloadAction<{ id: string }>) {
      const job = state.jobs.find((j) => j.id === action.payload.id);
      if (job) {
        job.open = false;
        job.minimized = false;
        job.downloadType = undefined;
      }
    },
    minimizeDownloadManager(state, action: PayloadAction<{ id: string }>) {
      const job = state.jobs.find((j) => j.id === action.payload.id);
      if (job) {
        job.minimized = true;
      }
    },
    minimizeDownloadManagerFeed(state, action: PayloadAction<{ feedId: string }>) {
      state.jobs.forEach((job) => {
        if (job.feedId === action.payload.feedId && isVisibleJob(job)) {
          job.minimized = true;
        }
      });
    },
    restoreDownloadManagerFeed(state, action: PayloadAction<{ feedId: string; id?: string }>) {
      let jobToFocus = action.payload.id ? state.jobs.find((job) => job.id === action.payload.id) : undefined;

      state.jobs.forEach((job) => {
        if (job.feedId === action.payload.feedId && isVisibleJob(job)) {
          job.minimized = false;
          if (!jobToFocus || (job.focusOrder || 0) > (jobToFocus.focusOrder || 0)) {
            jobToFocus = job;
          }
        }
      });

      if (jobToFocus) {
        jobToFocus.focusOrder = nextFocusOrder(state);
      }
    },
  },
});

export const {
  openDownloadManager,
  addDownloadManagerLayer,
  focusDownloadManager,
  setDownloadManagerType,
  closeDownloadManager,
  minimizeDownloadManager,
  minimizeDownloadManagerFeed,
  restoreDownloadManagerFeed,
} = downloadManagerSlice.actions;
export default downloadManagerSlice.reducer;
